import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Jumbotron, Button, Notifier, notify, PageLoader } from '@scholastic/volume-react';

import { parseProducts, ProductsCheckbox } from '../../utils/dataparsers.util';
import { fetchProductInfo } from '../../state/Home.api';
import { StoreContext } from '../../state/store';
import { downloadGroupLevelReports } from '../../state/CounterReports.api';

import {
  getCustomerInfoFromAPIResponse,
  getProductsFromAPIResponse,
  ProductsAPIResponse,
  SearchableDropdownValue,
} from '../Home/Home.dataparser';

import CounterReportsDropdown, {
  TypeOfCounterReport,
} from './CounterReportsDropdown/CounterReportsDropdown';
import ReportYearDropdown from './ReportYearDropdown/ReportYearDropdown';

import './CounterReports.scss';

const calculateYearsFrom2001 = (currentYear: number) => {
  if (currentYear < 2001) return [];

  const tempArray = [];

  for (let i: number = currentYear; i >= 2001; i--) {
    tempArray.push({
      label: i.toString(),
      value: i.toString(),
    });
  }

  return tempArray;
};

const CounterReports = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [counterReportType, setCounterReportType] = useState<TypeOfCounterReport>('');
  const yearNow = new Date().getFullYear();

  const dateDropdownValues: Array<SearchableDropdownValue> = useMemo(
    () => calculateYearsFrom2001(yearNow),
    [yearNow],
  );

  const [selectedReportYear, setReportYear] = useState<SearchableDropdownValue>({
    label: yearNow.toString(),
    value: yearNow.toString(),
  });

  const { state } = useContext(StoreContext);
  const customer = state.customerInfo;

  const [products, setProducts] = useState<ProductsAPIResponse | null>(null);
  const [parsedProducts, setParsedProducts] = useState<Array<ProductsCheckbox>>([]);

  const getProducts = async (): Promise<void> => {
    try {
      const productsInfo: ProductsAPIResponse = await fetchProductInfo();
      setProducts(productsInfo);
    } catch (err) {
      notify.warning('Something went wrong, please try again later');
      console.error(err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProducts();
  }, []);

  useEffect(() => {
    if (products && customer) {
      const rawProducts: { [key: string]: string } | null = getProductsFromAPIResponse(products);
      const customerProducts: Array<string> | undefined =
        getCustomerInfoFromAPIResponse(customer)?.products;
      const goProducts: Array<string> | undefined =
        getCustomerInfoFromAPIResponse(customer)?.goproducts;
      setParsedProducts(
        parseProducts(customerProducts, rawProducts).concat(parseProducts(goProducts, rawProducts)),
      );
    }
  }, [customer, products]);

  const sendRequest = () => {
    if (counterReportType && selectedReportYear) {
      setIsLoading(true);

      const request: any = {
        createRptBtn: 'Create Report',
        customerid: customer.result.customerid,
        outformat: 'counter',
        pcodesgood: true,
        reporttype: counterReportType,
        year: selectedReportYear.value,
      };

      parsedProducts.map((product: ProductsCheckbox) => {
        request[`show${product.value}`] = 'yes';
        return {};
      });

      downloadGroupLevelReports(request, hideLoading);
    } else {
      notify.warning('Please provide correct Report Type and Report Year');
    }
  };

  const hideLoading = () => {
    setIsLoading(false);
  };

  return (
    <div className="stats-counter-report">
      {isLoading && (
        <div>
          <PageLoader color={'dark'} size={'large'} type={'border'} />
        </div>
      )}

      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />

      <div className="stats-counter-report__text-container stats-counter-report__text-container--nobackground">
        <Jumbotron tag="div">
          <h1 className="stats-counter-report__title">Counter Reports</h1>
          <p className="stats-counter-report--normal-font">
            To download an overall report for your account, select the COUNTER report you wish to
            view, the report year, and click on Create Report.
          </p>
        </Jumbotron>
      </div>

      <div className="stats-counter-report__options-holder">
        <div className="stats-counter-report__option--large">
          <div className="stats-counter-report__option-label">
            Select the COUNTER report you wish to view:
          </div>
          <div className="stats-counter-report__option-dropdown" >
            <CounterReportsDropdown onSelect={setCounterReportType} />
          </div>
        </div>

        <div className="stats-counter-report__option--small">
          <div className="stats-counter-report__option-label">Select the report year:</div>
          <div className="stats-counter-report__option-dropdown" id="counterReportYear">
            <ReportYearDropdown availableDates={dateDropdownValues} onSelect={setReportYear} />
          </div>
        </div>
      </div>

      <div>
        <Button
          id="counterReportSubmit"
          disabled={isLoading}
          color="info"
          className="float-right mt-2"
          onClick={sendRequest}
        >
          Create Report
        </Button>
      </div>
    </div>
  );
};

export default CounterReports;
